import { fetchMultipleExchangeRates, hasApiKey } from './yahooFinance';
import { getCurrentDate, isSameDate } from '../utils/helpers';
import toast from 'react-hot-toast';

/**
 * Check if exchange rates need to be refreshed (not updated today)
 * @param {Object} exchangeRates - Exchange rates object with lastUpdated field
 * @returns {boolean} True if rates need refresh
 */
export const needsExchangeRateRefresh = (exchangeRates) => {
  if (!exchangeRates || !exchangeRates.lastUpdated) {
    return true; // No rates or never updated
  }

  const today = getCurrentDate();
  return !isSameDate(exchangeRates.lastUpdated, today);
};

/**
 * Refresh exchange rates from Yahoo Finance API
 * @param {string} baseCurrency - Base currency code (default: 'GBP')
 * @param {Array<string>} targetCurrencies - Array of currencies to fetch rates for
 * @param {Function} updateExchangeRates - Function to update exchange rates in state
 * @returns {Promise<Object>} Result with success/error status
 */
export const refreshExchangeRates = async (
  baseCurrency = 'GBP',
  targetCurrencies = ['USD', 'GBP'],
  updateExchangeRates
) => {
  try {
    if (!hasApiKey()) {
      console.log('No RapidAPI key configured, skipping exchange rate refresh');
      return {
        success: false,
        error: 'RapidAPI key not configured',
        skipped: true,
      };
    }

    console.log(`Fetching exchange rates for base currency: ${baseCurrency}`);

    // Fetch rates from Yahoo Finance
    const { rates, errors } = await fetchMultipleExchangeRates(baseCurrency, targetCurrencies);

    // Build the exchange rates object
    const ratesData = {
      baseCurrency: baseCurrency,
      rates: rates,
      lastUpdated: getCurrentDate(),
    };

    // Update the exchange rates in state
    await updateExchangeRates(ratesData);

    return {
      success: true,
      ratesCount: Object.keys(rates).length,
      baseCurrency: baseCurrency,
      lastUpdated: ratesData.lastUpdated,
      errors: errors.length > 0 ? errors : null,
    };

  } catch (error) {
    console.error('Failed to refresh exchange rates:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Main function: Refresh exchange rates if needed
 * @param {Object} params - Object containing exchange rates data and update function
 * @param {Object} params.exchangeRates - Current exchange rates object
 * @param {Function} params.updateExchangeRates - Function to update exchange rates
 * @param {string} params.baseCurrency - Base currency code (optional, defaults to current base)
 * @param {Array<string>} params.targetCurrencies - Currencies to fetch (optional)
 * @param {boolean} params.silent - If true, don't show toast notifications (default: false)
 * @param {boolean} params.forceRefresh - Force refresh even if updated today (default: false)
 * @returns {Promise<Object>} Result summary
 */
export const refreshExchangeRatesIfNeeded = async ({
  exchangeRates,
  updateExchangeRates,
  baseCurrency = null,
  targetCurrencies = ['USD', 'GBP'],
  silent = false,
  forceRefresh = false,
}) => {
  const results = {
    refreshed: false,
    ratesCount: 0,
    error: null,
    skipped: false,
  };

  // Determine base currency (use current or provided)
  const targetBaseCurrency = baseCurrency || exchangeRates?.baseCurrency || 'GBP';

  // Check if refresh is needed
  const needsRefresh = forceRefresh || needsExchangeRateRefresh(exchangeRates);

  if (!needsRefresh) {
    if (!silent) {
      console.log('Exchange rates are already up to date');
    }
    return results;
  }

  if (!silent) {
    console.log(forceRefresh ? 'Force refreshing exchange rates...' : 'Exchange rates need refresh, updating...');
  }

  // Perform the refresh
  const refreshResult = await refreshExchangeRates(
    targetBaseCurrency,
    targetCurrencies,
    updateExchangeRates
  );

  results.refreshed = refreshResult.success;
  results.ratesCount = refreshResult.ratesCount || 0;
  results.error = refreshResult.error || null;
  results.skipped = refreshResult.skipped || false;

  // Show notifications (unless silent)
  if (!silent && !results.skipped) {
    if (refreshResult.success) {
      const message = refreshResult.errors && refreshResult.errors.length > 0
        ? `Updated ${refreshResult.ratesCount} exchange rates (${refreshResult.errors.length} failed)`
        : `Updated ${refreshResult.ratesCount} exchange rates`;
      toast.success(message);
    } else {
      toast.error(`Failed to update exchange rates: ${refreshResult.error}`);
    }
  }

  return results;
};

/**
 * Check how old the current exchange rates are
 * @param {Object} exchangeRates - Exchange rates object with lastUpdated field
 * @returns {number} Number of days since last update (-1 if never updated)
 */
export const getExchangeRateAge = (exchangeRates) => {
  if (!exchangeRates || !exchangeRates.lastUpdated) {
    return -1;
  }

  const today = new Date();
  const lastUpdate = new Date(exchangeRates.lastUpdated);
  const diffTime = Math.abs(today - lastUpdate);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
};
